import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import "./WebhookSetting.css";

const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const WebhookSettingLayer = () => {
  const [toast, setToast] = useState(null);
  const [savingFb, setSavingFb] = useState(false);
  const [savingIg, setSavingIg] = useState(false);
  const [syncingFb, setSyncingFb] = useState(false);
  const [syncingIg, setSyncingIg] = useState(false);
  const [connecting, setConnecting] = useState(null);
  const [checkingPerms, setCheckingPerms] = useState(false);
  const [oauthInfo, setOauthInfo] = useState({
    configured: false,
    has_config_id: false,
    verify_token: "",
    callback_url: "",
    facebook_permissions: [],
    instagram_permissions: [],
  });
  const [permissions, setPermissions] = useState({
    facebook: null,
    instagram: null,
  });
  const [facebookWebhook, setFacebookWebhook] = useState({
    type: "facebook",
    access_token: "",
    page_access_token: "",
    app_id: "",
  });
  const [instagramWebhook, setInstagramWebhook] = useState({
    type: "instagram",
    access_token: "",
    page_access_token: "",
    app_id: "",
  });
  const [whatsappCloud, setWhatsappCloud] = useState({
    phone_number_id: "",
    whatsapp_token: "",
    has_token: false,
    webhook_url: "",
    verify_token: "",
    connected: false,
  });
  const [savingWa, setSavingWa] = useState(false);
  const [connectingWa, setConnectingWa] = useState(false);
  const waSessionRef = useRef({ phone_number_id: "", waba_id: "" });

  const loadFacebookSdk = (appId) =>
    new Promise((resolve, reject) => {
      if (!appId) {
        reject(new Error("Facebook App ID missing in Admin Settings"));
        return;
      }
      if (window.FB) {
        window.FB.init({ appId, cookie: true, xfbml: false, version: "v21.0" });
        resolve(window.FB);
        return;
      }
      window.fbAsyncInit = function () {
        window.FB.init({ appId, cookie: true, xfbml: false, version: "v21.0" });
        resolve(window.FB);
      };
      if (document.getElementById("facebook-jssdk")) return;
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.onerror = () => reject(new Error("Failed to load Facebook SDK"));
      document.body.appendChild(script);
    });

  const connectWhatsAppCloud = async () => {
    if (!whatsappCloud.connect_ready || !whatsappCloud.app_id || !whatsappCloud.config_id) {
      showToast(
        "Add WhatsApp Embedded Signup Config ID in Admin → Site Settings first",
        "danger"
      );
      return;
    }
    setConnectingWa(true);
    waSessionRef.current = { phone_number_id: "", waba_id: "" };
    try {
      const FB = await loadFacebookSdk(whatsappCloud.app_id);
      // Facebook SDK rejects async callbacks ("Expression is of type asyncfunction")
      FB.login(
        function (response) {
          const code = response?.authResponse?.code;
          if (!code) {
            showToast(
              response?.error_message || "WhatsApp signup cancelled or failed",
              "danger"
            );
            setConnectingWa(false);
            return;
          }
          const session = waSessionRef.current || {};
          apiCall({
            url: "webhook/oauth/whatsapp/complete",
            method: "POST",
            body: {
              code,
              phone_number_id: session.phone_number_id || "",
              waba_id: session.waba_id || "",
            },
          })
            .then((result) => {
              showToast(
                result.msg || (result.success ? "WhatsApp Cloud connected" : "Connect failed"),
                result.success ? "success" : "danger"
              );
              if (result.success) {
                setWhatsappCloud((prev) => ({
                  ...prev,
                  connected: true,
                  has_token: true,
                  phone_number_id: result.data?.phone_number_id || prev.phone_number_id,
                  whatsapp_token: "",
                }));
                fetchWebookData();
              }
            })
            .catch((err) => {
              showToast(err.message || "WhatsApp connect failed", "danger");
            })
            .finally(() => {
              setConnectingWa(false);
            });
        },
        {
          config_id: whatsappCloud.config_id,
          response_type: "code",
          override_default_response_type: true,
          extras: { setup: {}, featureType: "" },
        }
      );
    } catch (error) {
      showToast(error.message || "Could not open WhatsApp signup", "danger");
      setConnectingWa(false);
    }
  };

  const showToast = (message, cls = "success") => {
    setToast({ message, class: cls });
    setTimeout(() => setToast(null), 2600);
  };

  const facebookUrl = useMemo(
    () =>
      facebookWebhook.access_token
        ? `${apiBase}/webhook/facebook/${facebookWebhook.access_token}`
        : `${apiBase}/webhook/facebook/{verify_token}`,
    [facebookWebhook.access_token]
  );

  const instagramUrl = useMemo(
    () =>
      instagramWebhook.access_token
        ? `${apiBase}/webhook/instagram/${instagramWebhook.access_token}`
        : `${apiBase}/webhook/instagram/{verify_token}`,
    [instagramWebhook.access_token]
  );

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied`);
    } catch {
      showToast("Could not copy", "danger");
    }
  };

  const fetchWebookData = async () => {
    try {
      const response = await apiCall({
        url: `webhook/get-config`,
        method: "GET",
      });
      if (response?.data) {
        if (response.data.facebook) {
          setFacebookWebhook((prev) => ({ ...prev, ...response.data.facebook }));
        }
        if (response.data.instagram) {
          setInstagramWebhook((prev) => ({ ...prev, ...response.data.instagram }));
        }
        if (response.data.oauth) setOauthInfo((prev) => ({ ...prev, ...response.data.oauth }));
        if (response.data.permissions) {
          setPermissions({
            facebook: response.data.permissions.facebook || null,
            instagram: response.data.permissions.instagram || null,
          });
        }
        if (response.data.whatsapp_cloud) {
          setWhatsappCloud((prev) => ({
            ...prev,
            ...response.data.whatsapp_cloud,
            // never echo full token from API — user re-enters to rotate
            whatsapp_token: "",
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching webhook config:", error);
    }
  };

  const handleWhatsappCloudSubmit = async (e) => {
    e.preventDefault();
    if (!whatsappCloud.phone_number_id?.trim() || !whatsappCloud.whatsapp_token?.trim()) {
      showToast("Enter Phone Number ID and permanent WhatsApp token", "danger");
      return;
    }
    setSavingWa(true);
    try {
      const response = await apiCall({
        url: `user/update_meta_creds`,
        method: "POST",
        body: {
          phone_number_id: whatsappCloud.phone_number_id.trim(),
          whatsapp_token: whatsappCloud.whatsapp_token.trim(),
        },
      });
      showToast(response.msg || "WhatsApp Cloud saved", response.success ? "success" : "danger");
      if (response.success) {
        setWhatsappCloud((prev) => ({ ...prev, has_token: true, connected: true, whatsapp_token: "" }));
        fetchWebookData();
      }
    } catch (error) {
      showToast(error.message || "Save failed", "danger");
    } finally {
      setSavingWa(false);
    }
  };

  const refreshPermissions = async (type) => {
    setCheckingPerms(true);
    try {
      const response = await apiCall({
        url: `webhook/oauth/permissions?type=${type}`,
        method: "GET",
      });
      if (response?.success && response.data) {
        setPermissions((prev) => ({ ...prev, [type]: response.data }));
        showToast(response.data.msg || "Permissions checked", response.data.ready ? "success" : "danger");
      } else {
        showToast(response?.msg || "Could not check permissions", "danger");
      }
    } catch (error) {
      showToast(error.message || "Permission check failed", "danger");
    } finally {
      setCheckingPerms(false);
    }
  };

  const connectMeta = async (type, { grant = false } = {}) => {
    setConnecting(grant ? `grant-${type}` : type);
    try {
      const response = await apiCall({
        url: `webhook/oauth/${type}/url${grant ? "?grant=1" : ""}`,
        method: "GET",
      });
      if (!response?.success || !response.url) {
        showToast(response?.msg || "Could not start Meta connect", "danger");
        return;
      }
      const popup = window.open(
        response.url,
        "meta_oauth",
        "width=640,height=760,menubar=no,toolbar=no,status=no"
      );
      if (!popup) {
        showToast("Popup blocked — allow popups for this site", "danger");
      } else if (grant) {
        showToast("Allow every Page permission in the Facebook popup, then come back here");
      }
    } catch (error) {
      showToast(error.message || "Connect failed", "danger");
    } finally {
      setConnecting(null);
    }
  };

  const syncInstagramConversationList = async () => {
    setSyncingIg(true);
    try {
      const response = await apiCall({
        url: `social/sync-ig-conversation-list`,
        method: "GET",
      });
      showToast(response?.msg || (response?.success ? "Instagram chats synced" : "Sync finished"), response?.success === false ? "danger" : "success");
    } catch (error) {
      showToast(error.message || "Sync failed", "danger");
    } finally {
      setSyncingIg(false);
    }
  };

  const syncFacebookConversationList = async () => {
    setSyncingFb(true);
    try {
      const response = await apiCall({
        url: `social/sync-fb-conversation-list`,
        method: "GET",
      });
      showToast(
        response?.msg ||
          response?.data?.msg ||
          (response?.success ? "Facebook chats synced" : "Sync finished"),
        response?.success === false ? "danger" : "success"
      );
    } catch (error) {
      showToast(error.message || "Sync failed", "danger");
    } finally {
      setSyncingFb(false);
    }
  };

  const handleFacebookSubmit = async (e) => {
    e.preventDefault();
    setSavingFb(true);
    try {
      const response = await apiCall({
        url: `webhook/facebook/config`,
        method: "POST",
        body: facebookWebhook,
      });
      showToast(response.msg || "Facebook settings saved", response.success ? "success" : "danger");
    } catch (error) {
      showToast(error.message, "danger");
    } finally {
      setSavingFb(false);
    }
  };

  const handleInstagramSubmit = async (e) => {
    e.preventDefault();
    setSavingIg(true);
    try {
      const response = await apiCall({
        url: `webhook/instagram/config`,
        method: "POST",
        body: instagramWebhook,
      });
      showToast(response.msg || "Instagram settings saved", response.success ? "success" : "danger");
    } catch (error) {
      showToast(error.message, "danger");
    } finally {
      setSavingIg(false);
    }
  };

  useEffect(() => {
    document.title = "Webhook Settings";
    fetchWebookData();

    const params = new URLSearchParams(window.location.search);
    if (params.get("oauth") === "success") {
      showToast(`${params.get("channel") || "Meta"} connected`);
      window.history.replaceState({}, "", "/webhook-settings");
      fetchWebookData();
    } else if (params.get("oauth") === "error") {
      showToast(params.get("msg") || "OAuth failed", "danger");
      window.history.replaceState({}, "", "/webhook-settings");
    }

    const onMsg = (event) => {
      // Messenger/Instagram popup oauth
      if (event?.data?.type === "meta_oauth") {
        if (event.data.success) {
          showToast(
            `${event.data.channel === "instagram" ? "Instagram" : "Facebook"} connected${
              event.data.pageName ? ` (${event.data.pageName})` : ""
            }`
          );
          fetchWebookData();
        } else {
          showToast(event.data.message || "OAuth failed", "danger");
        }
        return;
      }

      // WhatsApp Embedded Signup session info from Meta
      const origin = event.origin || "";
      if (
        origin !== "https://www.facebook.com" &&
        origin !== "https://web.facebook.com" &&
        origin !== "https://facebook.com"
      ) {
        return;
      }
      try {
        const raw = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (raw?.type !== "WA_EMBEDDED_SIGNUP") return;
        const payload = raw.data || {};
        if (payload.phone_number_id || payload.waba_id) {
          waSessionRef.current = {
            phone_number_id: payload.phone_number_id || "",
            waba_id: payload.waba_id || "",
          };
        }
      } catch {
        /* ignore non-json */
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  return (
    <div className="wh-page">
      {toast && (
        <div className={`alert alert-${toast.class} wh-toast`}>{toast.message}</div>
      )}

      <section className="wh-hero">
        <h2>Connect Messenger channels</h2>
        <p>
          Click Connect to authorize with Facebook Login — we save your Page / Instagram
          tokens automatically. You can still edit tokens manually below.
        </p>
        <div className="wh-hero-chips">
          <span className="wh-chip">
            <Icon icon="mdi:facebook" width={15} />
            Facebook Messenger
          </span>
          <span className="wh-chip">
            <Icon icon="mdi:instagram" width={15} />
            Instagram Messaging
          </span>
          <span className="wh-chip">
            <Icon icon={oauthInfo.configured ? "mdi:check-circle" : "mdi:alert-circle-outline"} width={15} />
            {oauthInfo.configured ? "OAuth ready" : "Configure Meta App in Admin Settings"}
          </span>
        </div>
      </section>

      <section className="wh-perms">
        <div className="wh-perms-head">
          <div>
            <h3>Meta permissions</h3>
            <p>
              Sync &amp; send need <code>pages_messaging</code>. Grant permissions here, then allow
              every Page permission in the Facebook popup.
            </p>
          </div>
          <button
            type="button"
            className="wh-btn wh-btn-ghost"
            disabled={checkingPerms}
            onClick={() => {
              refreshPermissions("facebook");
              refreshPermissions("instagram");
            }}
          >
            <Icon icon="mdi:refresh" width={18} />
            {checkingPerms ? "Checking…" : "Recheck"}
          </button>
        </div>

        <div className="wh-perms-grid">
          {[
            {
              key: "facebook",
              title: "Facebook Messenger",
              icon: "mdi:facebook",
              cls: "fb",
              fallback: oauthInfo.facebook_permissions,
            },
            {
              key: "instagram",
              title: "Instagram Messaging",
              icon: "mdi:instagram",
              cls: "ig",
              fallback: oauthInfo.instagram_permissions,
            },
          ].map((channel) => {
            const status = permissions[channel.key];
            const list = status?.required?.length ? status.required : channel.fallback || [];
            const granted = new Set(status?.granted || []);
            const missing = new Set(status?.missing || []);
            return (
              <div key={channel.key} className={`wh-perms-card ${channel.cls}`}>
                <div className="wh-perms-card-top">
                  <div className={`wh-brand ${channel.cls}`}>
                    <Icon icon={channel.icon} width={22} />
                  </div>
                  <div>
                    <h4>{channel.title}</h4>
                    <p>
                      {status?.ready
                        ? "Ready"
                        : status?.connected
                          ? "Missing permissions"
                          : "Not connected"}
                      {status?.pageId ? ` · Page ${status.pageId}` : ""}
                    </p>
                  </div>
                </div>

                <ul className="wh-perm-list">
                  {list.map((perm) => {
                    const key = perm.key || perm;
                    const label = perm.label || key;
                    const ok = granted.has(key) && !missing.has(key);
                    const bad = missing.has(key);
                    return (
                      <li key={key} className={ok ? "ok" : bad ? "bad" : ""}>
                        <Icon
                          icon={ok ? "mdi:check-circle" : bad ? "mdi:close-circle" : "mdi:circle-outline"}
                          width={16}
                        />
                        <span>
                          <strong>{key}</strong>
                          <em>{label}</em>
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {status?.msg ? <p className="wh-perms-msg">{status.msg}</p> : null}

                <div className="wh-perms-actions">
                  <button
                    type="button"
                    className={`wh-btn wh-connect ${channel.cls}`}
                    disabled={!!connecting || !oauthInfo.configured}
                    onClick={() => connectMeta(channel.key, { grant: true })}
                  >
                    <Icon icon="mdi:shield-key-outline" width={18} />
                    {connecting === `grant-${channel.key}`
                      ? "Opening Facebook…"
                      : "Grant permissions"}
                  </button>
                  <button
                    type="button"
                    className="wh-btn wh-btn-ghost"
                    disabled={checkingPerms}
                    onClick={() => refreshPermissions(channel.key)}
                  >
                    Check status
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="wh-grid">
        <article className="wh-card">
          <div className="wh-card-head fb">
            <div className="wh-brand fb">
              <Icon icon="mdi:facebook" width={24} />
            </div>
            <div>
              <h3>Facebook Messenger</h3>
              <p>Page webhook + inbox sync for Facebook messages.</p>
            </div>
          </div>
          <form className="wh-card-body" onSubmit={handleFacebookSubmit}>
            <button
              type="button"
              className="wh-btn wh-connect fb"
              disabled={!!connecting}
              onClick={() => connectMeta("facebook")}
            >
              <Icon icon="mdi:facebook" width={20} />
              {connecting === "facebook"
                ? "Opening Facebook…"
                : facebookWebhook.page_access_token
                  ? "Reconnect Facebook"
                  : "Connect Facebook"}
            </button>
            {facebookWebhook.page_access_token ? (
              <p className="wh-hint wh-connected">
                <Icon icon="mdi:check-circle" width={16} /> Connected
                {facebookWebhook.app_id ? ` · Page ${facebookWebhook.app_id}` : ""}
              </p>
            ) : null}
            <div className="wh-field">
              <label>Callback URL</label>
              <div className="wh-url-row">
                <input className="form-control" readOnly value={facebookUrl} />
                <button
                  type="button"
                  className="wh-copy"
                  title="Copy URL"
                  onClick={() => copyText(facebookUrl, "Facebook webhook URL")}
                >
                  <Icon icon="mdi:content-copy" width={18} />
                </button>
              </div>
              <p className="wh-hint mt-1">Use this as the Callback URL in Meta → Webhooks.</p>
            </div>
            <div className="wh-field">
              <label htmlFor="fb-verify">Verify access token</label>
              <input
                id="fb-verify"
                className="form-control"
                placeholder="Verify token"
                value={facebookWebhook.access_token}
                onChange={(e) =>
                  setFacebookWebhook({ ...facebookWebhook, access_token: e.target.value })
                }
              />
            </div>
            <div className="wh-field">
              <label htmlFor="fb-page">Page access token</label>
              <input
                id="fb-page"
                className="form-control"
                placeholder="Page access token"
                value={facebookWebhook.page_access_token}
                onChange={(e) =>
                  setFacebookWebhook({ ...facebookWebhook, page_access_token: e.target.value })
                }
              />
            </div>
            <div className="wh-field">
              <label htmlFor="fb-app">App ID</label>
              <input
                id="fb-app"
                className="form-control"
                placeholder="Meta App ID"
                value={facebookWebhook.app_id}
                onChange={(e) =>
                  setFacebookWebhook({ ...facebookWebhook, app_id: e.target.value })
                }
              />
            </div>
            <div className="wh-actions">
              <button type="submit" className="wh-btn wh-btn-primary fb" disabled={savingFb}>
                <Icon icon="mdi:content-save-outline" width={18} />
                {savingFb ? "Saving…" : "Save Facebook settings"}
              </button>
              <button
                type="button"
                className="wh-btn wh-btn-ghost"
                disabled={syncingFb}
                onClick={syncFacebookConversationList}
              >
                <Icon icon="mdi:sync" width={18} />
                {syncingFb ? "Syncing…" : "Sync Facebook chats"}
              </button>
            </div>
          </form>
        </article>

        <article className="wh-card">
          <div className="wh-card-head ig">
            <div className="wh-brand ig">
              <Icon icon="mdi:instagram" width={24} />
            </div>
            <div>
              <h3>Instagram Messaging</h3>
              <p>Instagram DM webhook + conversation sync.</p>
            </div>
          </div>
          <form className="wh-card-body" onSubmit={handleInstagramSubmit}>
            <button
              type="button"
              className="wh-btn wh-connect ig"
              disabled={!!connecting}
              onClick={() => connectMeta("instagram")}
            >
              <Icon icon="mdi:instagram" width={20} />
              {connecting === "instagram"
                ? "Opening Instagram…"
                : instagramWebhook.page_access_token
                  ? "Reconnect Instagram"
                  : "Connect Instagram"}
            </button>
            {instagramWebhook.page_access_token ? (
              <p className="wh-hint wh-connected">
                <Icon icon="mdi:check-circle" width={16} /> Connected
                {instagramWebhook.app_id ? ` · IG ${instagramWebhook.app_id}` : ""}
              </p>
            ) : null}
            <div className="wh-field">
              <label>Callback URL</label>
              <div className="wh-url-row">
                <input className="form-control" readOnly value={instagramUrl} />
                <button
                  type="button"
                  className="wh-copy"
                  title="Copy URL"
                  onClick={() => copyText(instagramUrl, "Instagram webhook URL")}
                >
                  <Icon icon="mdi:content-copy" width={18} />
                </button>
              </div>
              <p className="wh-hint mt-1">Paste into Meta Instagram messaging webhooks.</p>
            </div>
            <div className="wh-field">
              <label htmlFor="ig-verify">Verify access token</label>
              <input
                id="ig-verify"
                className="form-control"
                placeholder="Verify token"
                value={instagramWebhook.access_token}
                onChange={(e) =>
                  setInstagramWebhook({ ...instagramWebhook, access_token: e.target.value })
                }
              />
            </div>
            <div className="wh-field">
              <label htmlFor="ig-page">Page access token</label>
              <input
                id="ig-page"
                className="form-control"
                placeholder="Page access token"
                value={instagramWebhook.page_access_token}
                onChange={(e) =>
                  setInstagramWebhook({
                    ...instagramWebhook,
                    page_access_token: e.target.value,
                  })
                }
              />
            </div>
            <div className="wh-field">
              <label htmlFor="ig-app">Page ID</label>
              <input
                id="ig-app"
                className="form-control"
                placeholder="Instagram / Page ID"
                value={instagramWebhook.app_id}
                onChange={(e) =>
                  setInstagramWebhook({ ...instagramWebhook, app_id: e.target.value })
                }
              />
            </div>
            <div className="wh-actions">
              <button type="submit" className="wh-btn wh-btn-primary ig" disabled={savingIg}>
                <Icon icon="mdi:content-save-outline" width={18} />
                {savingIg ? "Saving…" : "Save Instagram settings"}
              </button>
              <button
                type="button"
                className="wh-btn wh-btn-ghost"
                disabled={syncingIg}
                onClick={syncInstagramConversationList}
              >
                <Icon icon="mdi:sync" width={18} />
                {syncingIg ? "Syncing…" : "Sync Instagram chats"}
              </button>
            </div>
          </form>
        </article>
      </div>

      <article className="wh-card wh-card-wide">
        <div className="wh-card-head wa">
          <div className="wh-brand wa">
            <Icon icon="mdi:whatsapp" width={24} />
          </div>
          <div>
            <h3>WhatsApp Cloud API (Meta)</h3>
            <p>
              Official Meta WhatsApp Business API — different from QR/session WhatsApp.
              Create a WhatsApp Business app in Meta, then paste credentials below.
            </p>
          </div>
        </div>
        <form className="wh-card-body" onSubmit={handleWhatsappCloudSubmit}>
          <button
            type="button"
            className="wh-btn wh-connect wa"
            disabled={connectingWa || !whatsappCloud.connect_ready}
            onClick={connectWhatsAppCloud}
          >
            <Icon icon="mdi:whatsapp" width={20} />
            {connectingWa
              ? "Opening Meta…"
              : whatsappCloud.connected
                ? "Reconnect WhatsApp Cloud"
                : "Connect WhatsApp Cloud"}
          </button>
          {!whatsappCloud.connect_ready ? (
            <p className="wh-hint">
              Admin must set <strong>WhatsApp Embedded Signup Config ID</strong> in Site Settings
              (Meta → Facebook Login for Business → Create from template → WhatsApp Embedded Signup).
            </p>
          ) : (
            <p className="wh-hint">
              Click Connect — Meta opens Embedded Signup and we save Phone Number ID + token
              automatically.
            </p>
          )}

          {whatsappCloud.connected ? (
            <p className="wh-hint wh-connected">
              <Icon icon="mdi:check-circle" width={16} /> Connected
              {whatsappCloud.phone_number_id
                ? ` · Phone Number ID ${whatsappCloud.phone_number_id}`
                : ""}
            </p>
          ) : null}

          <div className="wh-field">
            <label>Webhook callback URL (Meta → WhatsApp → Configuration)</label>
            <div className="wh-url-row">
              <input className="form-control" readOnly value={whatsappCloud.webhook_url || ""} />
              <button
                type="button"
                className="wh-copy"
                title="Copy URL"
                onClick={() => copyText(whatsappCloud.webhook_url, "WhatsApp webhook URL")}
              >
                <Icon icon="mdi:content-copy" width={18} />
              </button>
            </div>
          </div>

          <div className="wh-field">
            <label>Verify token</label>
            <div className="wh-url-row">
              <input className="form-control" readOnly value={whatsappCloud.verify_token || ""} />
              <button
                type="button"
                className="wh-copy"
                title="Copy verify token"
                onClick={() => copyText(whatsappCloud.verify_token, "Verify token")}
              >
                <Icon icon="mdi:content-copy" width={18} />
              </button>
            </div>
            <p className="wh-hint mt-1">
              In Meta webhook setup, paste this exact Verify Token (your user uid).
            </p>
          </div>

          <div className="wh-field">
            <label htmlFor="wa-phone-id">Phone Number ID</label>
            <input
              id="wa-phone-id"
              className="form-control"
              placeholder="From Meta → WhatsApp → API Setup"
              value={whatsappCloud.phone_number_id}
              onChange={(e) =>
                setWhatsappCloud({ ...whatsappCloud, phone_number_id: e.target.value.trim() })
              }
            />
          </div>

          <div className="wh-field">
            <label htmlFor="wa-token">Permanent access token</label>
            <input
              id="wa-token"
              className="form-control"
              type="password"
              autoComplete="new-password"
              placeholder={
                whatsappCloud.has_token
                  ? "Token saved — paste a new one to replace"
                  : "System user permanent token from Meta Business"
              }
              value={whatsappCloud.whatsapp_token}
              onChange={(e) =>
                setWhatsappCloud({ ...whatsappCloud, whatsapp_token: e.target.value.trim() })
              }
            />
          </div>

          <ol className="wh-steps">
            <li>Meta Developers → create/select app → add <strong>WhatsApp</strong> product</li>
            <li>WhatsApp → API Setup → copy <strong>Phone number ID</strong></li>
            <li>
              Business Settings → System Users → generate <strong>permanent token</strong> with
              <code> whatsapp_business_messaging</code> + <code>whatsapp_business_management</code>
            </li>
            <li>
              WhatsApp → Configuration → Webhook → set Callback URL + Verify Token above →
              subscribe to <code>messages</code>
            </li>
            <li>Save credentials here</li>
          </ol>

          <div className="wh-actions">
            <button type="submit" className="wh-btn wh-btn-primary wa" disabled={savingWa}>
              <Icon icon="mdi:content-save-outline" width={18} />
              {savingWa ? "Saving…" : "Save WhatsApp Cloud settings"}
            </button>
          </div>
        </form>
      </article>
    </div>
  );
};

export default WebhookSettingLayer;
